import { useEffect, useRef } from "react";
import { useLanderConfig } from "../commons/ConfigContext";
import { sendErrorEvent } from "../commons/LanderEvent";
import { ErrorCode } from "../commons/Constants";
import { FeedType, LanderConfig } from "../types/LanderConfig";
import { getExpiryPartnerConfig } from "../commons/LanderConfig";

interface RsocBuildResult {
  url: string | null;
  error: boolean;
}

/**
 * Build RSOC redirect URL from landerConfig
 * Returns { url, error } - does not fire events (caller handles that)
 */
export function buildRsocRedirectUrl(
  landerConfig: LanderConfig
): RsocBuildResult {
  try {
    let rsocUrl: URL;
    if (landerConfig.rsoc.url) {
      rsocUrl = new URL(landerConfig.rsoc.url);
    } else {
      rsocUrl = new URL(landerConfig.rsoc.path, "https://www.searchhounds.com");
    }

    // Query params that will be used in searchhounds
    rsocUrl.searchParams.set("psystem", landerConfig.system);
    rsocUrl.searchParams.set("domain", window.location.hostname);

    const expiryPortfolioId = getExpiryPartnerConfig().portfolioId || "";
    if (expiryPortfolioId) rsocUrl.searchParams.set("pid", expiryPortfolioId);

    // Preserve original referrer - check query param first, then document.referrer
    // The oref query parameter may be set by JS redirect from parking-lander-tls-terminator
    // https://github.com/gdcorp-domains/parking-lander-tls-terminator
    const urlParams = new URLSearchParams(window.location.search);
    const orefParam = urlParams.get("oref");
    const originalReferrer = orefParam || document.referrer;
    if (originalReferrer) {
      rsocUrl.searchParams.set("oref", originalReferrer);
    }

    return { url: rsocUrl.toString(), error: false };
  } catch {
    return { url: null, error: true };
  }
}

interface RsocUrlResult {
  shouldRedirect: boolean;
  url: string | null;
}

export function useRsocUrl(): RsocUrlResult {
  const { landerConfig } = useLanderConfig();
  const eventFiredRef = useRef(false);

  const rsocPathOrUrlExists =
    !!landerConfig.rsoc?.path || !!landerConfig.rsoc?.url;
  const feedIDIsRsoc = landerConfig.lander?.feedID === FeedType.RSOC;
  // This domain was reported by Google for paid traffic. Temporary fix until we find a better way to detect paid traffic.
  const domainIsCloudynestStore =
    landerConfig.domain?.rootDomain === "cloudynest.store";

  const shouldRedirect =
    rsocPathOrUrlExists && feedIDIsRsoc && !domainIsCloudynestStore;

  const result = shouldRedirect
    ? buildRsocRedirectUrl(landerConfig)
    : { url: null, error: false };

  // Fire error event in useEffect (not during render)
  useEffect(() => {
    if (result.error && !eventFiredRef.current) {
      eventFiredRef.current = true;
      sendErrorEvent(landerConfig, ErrorCode.ERROR_RSOC_REDIRECT);
    }
  }, [result.error, landerConfig]);

  return { shouldRedirect, url: result.url };
}
